import api from './api.service';

export type ManagementExceptionCode =
  | 'CUSTOMER_ABOVE_CREDIT_LIMIT'
  | 'OVERDUE_RECEIVABLE'
  | 'STOCK_BELOW_REORDER'
  | 'DEAD_STOCK'
  | 'PROJECT_EXCEEDING_BUDGET'
  | 'EXCESS_MATERIAL_USAGE'
  | 'LEASE_EXPIRY_APPROACHING'
  | 'PURCHASE_PRICE_ABOVE_HISTORICAL'
  | 'OVERDUE_PAYABLE'
  | 'GRN_NOT_INVOICED'
  | 'SI_MATCH_VARIANCE_OR_HOLD'
  | 'OVERDUE_PO'
  | 'OPEN_BUDGET_EXCEEDANCE';

export type ManagementExceptionSeverity = 'critical' | 'warning' | 'info';

export interface ManagementExceptionItem {
  id: string;
  code: ManagementExceptionCode;
  severity: ManagementExceptionSeverity;
  title: string;
  message: string;
  entity_type: string;
  entity_id: string | null;
  entity_ref: string | null;
  amount: number | null;
  metric_value: number | null;
  threshold_value: number | null;
  days: number | null;
  link: string | null;
  area: string;
  detected_at: string;
  meta?: Record<string, unknown>;
}

export interface ManagementExceptionsResult {
  as_of: string;
  total: number;
  by_severity: { critical: number; warning: number; info: number };
  by_type: Array<{
    code: ManagementExceptionCode;
    label: string;
    count: number;
    critical: number;
    warning: number;
    info: number;
  }>;
  items: ManagementExceptionItem[];
}

export interface FacilityLease {
  id: string;
  company_id: string;
  lease_name: string;
  facility_reference?: string | null;
  lessor_name?: string | null;
  start_date?: string | null;
  end_date: string;
  notice_days?: number;
  annual_rent?: number | null;
  currency?: string | null;
  status?: string;
  notes?: string | null;
}

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: { data?: T } | T })?.data ?? res;
  return ((data as { data?: T })?.data ?? data) as T;
}

export const MANAGEMENT_EXCEPTION_TYPE_OPTIONS: Array<{ value: ManagementExceptionCode | ''; label: string }> = [
  { value: '', label: 'All types' },
  { value: 'CUSTOMER_ABOVE_CREDIT_LIMIT', label: 'Customer above credit limit' },
  { value: 'OVERDUE_RECEIVABLE', label: 'Overdue receivable' },
  { value: 'OVERDUE_PAYABLE', label: 'Overdue payable' },
  { value: 'STOCK_BELOW_REORDER', label: 'Stock below reorder' },
  { value: 'DEAD_STOCK', label: 'Dead stock' },
  { value: 'PROJECT_EXCEEDING_BUDGET', label: 'Project exceeding budget' },
  { value: 'OPEN_BUDGET_EXCEEDANCE', label: 'Open budget exceedance' },
  { value: 'EXCESS_MATERIAL_USAGE', label: 'Excess material usage' },
  { value: 'LEASE_EXPIRY_APPROACHING', label: 'Lease expiry approaching' },
  { value: 'PURCHASE_PRICE_ABOVE_HISTORICAL', label: 'Purchase price vs historical' },
  { value: 'GRN_NOT_INVOICED', label: 'GRN not invoiced' },
  { value: 'SI_MATCH_VARIANCE_OR_HOLD', label: 'SI match variance / hold' },
  { value: 'OVERDUE_PO', label: 'Overdue PO' },
];

const managementExceptionsService = {
  async getExceptions(params?: { codes?: string; limit?: number }): Promise<ManagementExceptionsResult> {
    const res = await api.get('/owner-intelligence/management-exceptions', { params });
    return unwrap<ManagementExceptionsResult>(res);
  },

  async listFacilityLeases(): Promise<FacilityLease[]> {
    const res = await api.get('/owner-intelligence/facility-leases');
    return unwrap<FacilityLease[]>(res) || [];
  },

  async upsertFacilityLease(payload: Partial<FacilityLease> & { lease_name: string; end_date: string }) {
    const res = await api.put('/owner-intelligence/facility-leases', payload);
    return unwrap<{ id: string }>(res);
  },

  async deleteFacilityLease(id: string) {
    const res = await api.delete(`/owner-intelligence/facility-leases/${id}`);
    return unwrap<{ id: string }>(res);
  },
};

export default managementExceptionsService;
